import {
    browserLocalPersistence,
    browserSessionPersistence,
    createUserWithEmailAndPassword,
    GoogleAuthProvider,
    onAuthStateChanged,
    onIdTokenChanged,
    sendPasswordResetEmail,
    setPersistence,
    signInWithEmailAndPassword,
    signInWithCustomToken,
    signInWithPopup,
    signOut,
    User,
    UserCredential
} from 'firebase/auth';
import { createContext, ReactNode, useContext, useEffect, useRef, useState } from "react";

import { apiConfig, APIIntegration } from "@integrations/APIIntegration";
import { Organization } from "@models/Organization";
import { isMobile } from "react-device-detect";
import { useNavigate } from 'react-router-dom';
import { firebaseAuth } from "./firebase";
import { Application } from '@core/Application';
import { SharedApplication } from '@shared/core/SharedApplication';
import { Role } from '@models/Permissions';
import type { AuthenticatedDashboardUser } from '@services/UsersService';

interface AuthContextType {
    user: User | null | undefined;
    dashboardUser: AuthenticatedDashboardUser | undefined;
    isSystemAdmin: boolean;
    validationStatus: string;
    signUp: (email: string, password: string) => Promise<UserCredential>;
    logIn: (email: string, password: string, rememberMe: boolean) => Promise<UserCredential>;
    logInWithCustomToken: (customToken: string, rememberMe: boolean) => Promise<UserCredential>;
    logOut: () => Promise<void>;
    logInWithGoogle: (rememberMe: boolean) => Promise<void>;
    resetPassword: (email: string) => Promise<void>;
    loading: boolean;
    setLoading: (loading: boolean) => void;
    validateWithQontact: (accessToken: string, uid: string, options?: { forceRefresh?: boolean }) => Promise<void>;
    orgs: Organization[];
    userRole: Role
    updateUserRole: (orgId: string) => Promise<void>;
}

interface DevAuthTools {
    logInWithCustomToken: (customToken: string, rememberMe?: boolean) => Promise<UserCredential>;
    signOut: () => Promise<void>;
}

declare global {
    interface Window {
        qontactDevAuth?: DevAuthTools;
    }
}

export const authContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
    const context = useContext(authContext);
    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
}

interface AuthProviderProps {
    children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps): JSX.Element {
    const [user, setUser] = useState<User | null | undefined>();
    const [dashboardUser, setDashboardUser] = useState<AuthenticatedDashboardUser | undefined>();
    const [isSystemAdmin, setIsSystemAdmin] = useState<boolean>(false);
    const [userRole, setUserRole] = useState<Role>(undefined);
    const [validationStatus, setValidationStatus] = useState<string>('');
    const [orgs, setOrgs] = useState<Organization[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const currentUserRef = useRef<User | null | undefined>(undefined);
    const validationStatusRef = useRef<string>('');

    const isDevAuthEnabled = true; // Set to false to disable dev auth tools in the UI and prevent any accidental usage

    const navigate = useNavigate();

    useEffect(() => {
        currentUserRef.current = user;
    }, [user]);

    useEffect(() => {
        validationStatusRef.current = validationStatus;
    }, [validationStatus]);

    const signUp = async (email: string, password: string): Promise<UserCredential> => {
        return await createUserWithEmailAndPassword(firebaseAuth, email, password);
    }

    const logIn = async (email: string, password: string, rememberMe: boolean): Promise<UserCredential> => {
        const persistence = rememberMe ? browserLocalPersistence : browserSessionPersistence;
        await setPersistence(firebaseAuth, persistence);
        const userCredentials = await signInWithEmailAndPassword(firebaseAuth, email, password);
        const accessToken = await userCredentials.user.getIdToken();
        await validateWithQontact(accessToken, userCredentials.user.uid);
        return userCredentials;
    };

    const logInWithCustomToken = async (customToken: string, rememberMe: boolean): Promise<UserCredential> => {
        const persistence = rememberMe ? browserLocalPersistence : browserSessionPersistence;
        await setPersistence(firebaseAuth, persistence);
        return await signInWithCustomToken(firebaseAuth, customToken);
    };

    const logInWithGoogle = async (rememberMe: boolean): Promise<void> => {
        const persistence = rememberMe ? browserLocalPersistence : browserSessionPersistence;  
        await setPersistence(firebaseAuth, persistence);  
        const googleProvider = new GoogleAuthProvider();
        // Changed to signInWithPopup for the time being, the userCred is caught in the onAuthStateChanged listener
        if (isMobile) {
            await signInWithPopup(firebaseAuth, googleProvider);
        } else {
            await signInWithPopup(firebaseAuth, googleProvider);
        }
    }

    /**
     * Sends Firebase's password reset email. Deliberately resolves even when the account
     * does not exist: surfacing `auth/user-not-found` would turn the reset form into an
     * email enumerator, since it is reachable without authenticating.
     */
    const resetPassword = async (email: string): Promise<void> => {
        try {
            await sendPasswordResetEmail(firebaseAuth, email);
        } catch (error: any) {
            if (error?.code === 'auth/user-not-found') {
                return;
            }
            throw error;
        }
    }

    interface OrganizationsResponse {
        organizations: Organization[];
        lastKey: string;
    }

    const validateWithQontact = async (
        accessToken: string,
        uid: string,
        options?: { forceRefresh?: boolean }
    ): Promise<void> => {
        const pageSize = 50;
        const usersService = Application.shared().coreDependencies.usersService;
        const authenticatedUser = await usersService.getAuthenticatedUser();
        setDashboardUser(authenticatedUser);
        setIsSystemAdmin(authenticatedUser?.isSystemAdmin === true);

        const fetchOrganizations = async (lastKey: string = ""): Promise<OrganizationsResponse> => {
            const dashboardService = Application.shared().coreDependencies.dashboardService;
            const result = await dashboardService.getOrganizationsPaginated(
                uid,
                pageSize.toString(),
                lastKey,
                options?.forceRefresh !== true
            );
            return result;
        };
      
        let allOrgs: Organization[] = [];
        let currentLastKey = "";
        let hasMore = true;
      
        while (hasMore) {
            const { organizations, lastKey: newLastKey } = await fetchOrganizations(currentLastKey);

            allOrgs = [...allOrgs, ...organizations];
            currentLastKey = newLastKey;
            hasMore = organizations.length === pageSize;
        }

        setOrgs(allOrgs);
        console.log(allOrgs);

        if (allOrgs.length > 0) {
            APIIntegration.setApiToken(accessToken);
            Application.shared().setToken(accessToken);
            SharedApplication.shared().setToken(accessToken);
            setValidationStatus('Validated');
        } else {
            setValidationStatus('No Organizations');
            navigate('/unauthorized');
        }
    }

    const updateUserRole = async (orgId: string): Promise<void> => {
        const dashboardService = Application.shared().coreDependencies.dashboardService;
        const role = await dashboardService.getUserRoleInOrganization(user.uid, orgId);
        setUserRole(role);
    }

    const logOut = async (): Promise<void> => {
        await signOut(firebaseAuth);
        setValidationStatus('None');
    }

    useEffect(() => {
        const unsubscribe = onIdTokenChanged(firebaseAuth, async (currentUser) => {
            setLoading(true);
            if (currentUser) {
                const token = await currentUser.getIdToken();
                APIIntegration.setApiToken(token);
                Application.shared().setToken(token);
                SharedApplication.shared().setToken(token);

                const shouldValidate =
                    !currentUserRef.current ||
                    currentUserRef.current.uid !== currentUser.uid ||
                    validationStatusRef.current !== 'Validated';

                if (shouldValidate) {
                    console.log('Validating with Qontact...');
                    await validateWithQontact(token, currentUser.uid);
                }

                setUser(currentUser);
            } else {
                APIIntegration.setApiToken('');
                Application.shared().setToken('');
                SharedApplication.shared().setToken('');
                setUser(null);
                setDashboardUser(undefined);
                setIsSystemAdmin(false);
                setValidationStatus('None');
            }
            
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    useEffect(() => {
        if (!isDevAuthEnabled) {
            delete window.qontactDevAuth;
            return;
        }

        window.qontactDevAuth = {
            logInWithCustomToken: async (customToken: string, rememberMe = false) => {
                if (!customToken?.trim()) {
                    throw new Error('Custom token is required.');
                }

                return await logInWithCustomToken(customToken, rememberMe);
            },
            signOut: logOut
        };

        return () => {
            delete window.qontactDevAuth;
        };
    }, [isDevAuthEnabled]);

    const value: AuthContextType = {
        user,
        dashboardUser,
        isSystemAdmin,
        validationStatus,
        signUp,
        logIn,
        logInWithCustomToken,
        logOut,
        logInWithGoogle,
        resetPassword,
        loading,
        setLoading,
        validateWithQontact,
        orgs,
        userRole,
        updateUserRole
    };
    
    return (
        <authContext.Provider value={value}>
            {children}
        </authContext.Provider>
    );
}
